import { Layout, message, PageHeader } from 'antd';
import moment from 'moment';
import {
  useCallback,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import UserContext from '../../contexts/user';
import { useRequest } from '../../hooks/useRequest';
import TruckLoadsFilter from './TruckLoadsPage.filter';
import TruckLoadsTable from './TruckLoadsPage.table';
import { IGetTruckLoadsResponse } from '../../structures/interfaces/TruckLoad';
import TruckLoadsController from '../../structures/controllers/TruckLoad';

const TruckLoadsPage = (): ReactElement => {
  const { t } = useTranslation();
  const { userData, customerData } = useContext(UserContext);

  const [truckLoads, setTruckLoads] = useState<IGetTruckLoadsResponse[]>([]);

  const [fetchLoadsRequest, isFetchingLoad] = useRequest(
    TruckLoadsController.getTruckLoads
  );

  const truckLoadStatus = [
    { label: t('pages.truckLoads.waitingDriver'), value: 'WAITING_DRIVER' },
    { label: t('pages.truckLoads.waitingComercial'), value: 'WAITING_RELEASE' },
    { label: t('pages.truckLoads.waitingActions'), value: 'WAITING_ACTIONS' },
    { label: t('pages.truckLoads.loaded'), value: 'LOADED' },
    { label: t('pages.truckLoads.loadReleased'), value: 'AUTHORIZED_LOAD' },
    { label: t('pages.truckLoads.canceled'), value: 'CANCELED' },
    { label: t('pages.truckLoads.expired'), value: 'EXPIRED' },
    { label: t('pages.truckLoads.loading'), value: 'LOADING' },
  ];

  const fetchLoads = useCallback(() => {
    return fetchLoadsRequest({})
      .then(res => {
        const sorted = res.sort((a, b) => {
          return moment(b.createdAt).unix() - moment(a.createdAt).unix();
        });
        setTruckLoads(sorted);
      })
      .catch(() => message.error('Não foram encontrados agendamentos!'));
  }, [userData, customerData]);

  const onCancelTruckLoadSuccess = useCallback((id: string) => {
    setTruckLoads(truckLoads => {
      const truckLoadToUpdate = truckLoads.find(
        truckLoad => truckLoad.id === id
      );
      if (truckLoadToUpdate) truckLoadToUpdate.status = 'CANCELED';
      return [...truckLoads];
    });
  }, []);

  useEffect(() => {
    fetchLoads();
  }, [fetchLoads]);

  return (
    <Layout>
      <PageHeader
        title={t('pages.truckLoads.title')}
        subTitle={t('pages.truckLoads.subtitle')}
        ghost={false}
      />
      <Layout.Content style={{ margin: 24 }}>
        <TruckLoadsFilter
          cardProps={{ style: { marginBottom: 24 } }}
          options={truckLoadStatus}
          truckLoads={truckLoads}
          setTruckLoads={setTruckLoads}
          fetchLoads={fetchLoads}
          isLoadingLoads={isFetchingLoad}
          customerData={customerData}
          userData={userData}
        />
        <TruckLoadsTable
          customerData={customerData}
          userData={userData}
          truckLoadStatus={truckLoadStatus}
          cardProps={{
            loading: isFetchingLoad,
            style: { marginBottom: 24 },
          }}
          tableProps={{ dataSource: truckLoads }}
          onCancelSuccessCallback={onCancelTruckLoadSuccess}
        />
      </Layout.Content>
    </Layout>
  );
};

export default TruckLoadsPage;
